/**
 * <add-item-form> Custom Element
 *
 * Wraps each `<form class="js-add-item-form">` rendered by
 * `add-item-form.liquid` (PDP main form, quick-add cards, and any other
 * caller). Owns the variant cache, form-state machine, ATC submit,
 * quantity stepper, waitlist flow, sticky-ATC forwarder, and the
 * radio-reclick deselect / fast-quick-add behaviors that used to live
 * in product-card.
 *
 * Markup:
 *   <add-item-form>
 *     <form class="js-add-item-form ..."> ... </form>
 *   </add-item-form>
 *
 * Events dispatched (bubbling CustomEvents on the element):
 *   - `variant:change` { detail: { variant, form } }
 *       After option/radio change, with the resolved variant (or null
 *       if the selection is incomplete / no match).
 *   - `cart:added` { detail: { item, form, hasHem } }
 *       After a successful /cart/add.js POST and any hemming follow-up.
 *   - `cart:error` { detail: { message, response, form } }
 *       After a failed /cart/add.js POST.
 *
 * Public API:
 *   - `getActiveVariant()` — returns the resolved variant or null.
 *   - `submit()` — programmatic submit; identical to user-clicked ATC
 *     except it skips the empty-option scroll-to-input affordance.
 *
 * Replaces `theme/js/modules/common-products-controller.js` (IIFE).
 *
 * Source path: theme/js/components/add-item-form.esm.js
 * Built name : ./add-item-form.js
 *
 * AI: Entirely written by AI
 * Date: 2026-05-06
 * Model: Claude Opus 4.7
 * Updated: 2026-06-02 (Claude Opus 4.8) - _loadUnavailableSelectOptions defers
 *   to ep:ready when called pre-hydration (so the flag + i18n disclaimer resolve
 *   on a cold load instead of throwing / rendering empty); reads the waitlist
 *   flag via appGet() and converted the onCartChanged flag read off app.flags().
 */

import { log } from './log.js';
import { appGet, isAppHydrated } from './app-get.js';
import { isSignedIn, loginUrlReturningTo } from './customer.js';
import {
  toggleFormEnabled,
  renderFormError,
  HAS_VARIANT_CLASS,
  FORM_LOADING_CLASS,
} from './form-state.js';
import {
  addToWaitlistCache,
  isVariantOnWaitlist,
  markWaitlistButton,
  resetWaitlistButton,
  WAITLIST_STORAGE_KEY,
} from './waitlist-cache.js';
import { addToWaitlist as bisAddToWaitlist } from './3sixteen-api.js';
import { push as notifierPush } from './notifier.js';
import { pjaxUrl } from './router.js';
import {
  handleSizeChange as hemmingHandleSizeChange,
  openHemTermsFork,
  closeHemPanel,
} from './hemming.js';
import {
  shouldShowModal as salePolicyShouldShowModal,
  openSalePolicyModal,
  resetForm as salePolicyResetForm,
} from './sale-policy.js';
import { getCurrentCart } from './cart-state.js';
import { addToCart, updateItems } from './cart-fetch.js';

const FORM_SELECTOR = '.js-add-item-form';
const PRODUCT_OPTION_ATTR = '[data-is-product-option="true"]';
const ADD_BUTTON_SELECTOR = '.js-add-to-cart';
const WAITLIST_BUTTON_SELECTOR = '.js-waitlist-button';
const QUANTITY_SPINNER_SELECTOR = '.js-quantity-spinner';
const QUICK_ADD_SELECTOR = '.js-quick-add-to-cart';
const PRODUCT_SELECTOR = '.js-product';
const STICKY_ATC_BUTTON_SELECTOR = '.js-sticky-add-to-cart button';
const COMING_SOON_CLASS = 'coming-soon';
const OPTION_UNAVAILABLE_CLASS = 'option-unavailable';

/**
 * Copy for a failed waitlist signup.
 *
 * `waitlistError` is the wrapper's machine-readable reason (see
 * 3sixteen-api.esm.js); otherwise the payload is BIS's own, whose `errors`
 * values arrive as arrays, as bare strings, and as null. Calling join()
 * straight on each value threw a TypeError on anything but an array — from
 * inside the error callback, before the form left `form-loading`, so the
 * waitlist button went permanently dead (every later click short-circuited
 * on the in-progress guard) with no message shown.
 *
 * AI: Entirely written by AI
 * Date: 2026-09-08
 */
function waitlistErrorMessage(response) {
  const generic = appGet(
    'i18n.notifications.waitlist.error',
    'Something went wrong. Please try again.'
  );
  if (response && response.waitlistError === 'no_email') {
    return appGet('i18n.notifications.waitlist.no_email', generic);
  }
  const parts = Object.values((response && response.errors) || {}).flatMap((value) => {
    if (Array.isArray(value)) return value;
    return typeof value === 'string' ? [value] : [];
  });
  return parts.filter(Boolean).join(' ') || generic;
}

class AddItemForm extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    const form = this.querySelector(FORM_SELECTOR);
    if (!form) {
      log('[Form] no form found in element', this);
      return;
    }
    this._form = form;
    this._variants = null;

    form.addEventListener('change', this._onChange);
    form.addEventListener('submit', this._onSubmit);
    form.addEventListener('click', this._onClick);

    this._handleComingSoon();
    this._loadUnavailableRadios();
    this._loadUnavailableSelectOptions();

    if (form.classList.contains(HAS_VARIANT_CLASS)) {
      this._refreshFormState();
    }

    this._bindStickyAtc();
    this._postLoginWaitlistRecovery();
  }

  disconnectedCallback() {
    if (this._stickyHandler && this._stickyAtcButton) {
      this._stickyAtcButton.removeEventListener('click', this._stickyHandler);
    }
  }

  // ── Public API ────────────────────────────────────────────────────────

  getActiveVariant() {
    return this._computeActiveVariant();
  }

  submit() {
    if (!this._form) return;
    if (typeof this._form.requestSubmit === 'function') {
      this._form.requestSubmit();
    } else {
      this._form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }
  }

  // ── Variant resolution ────────────────────────────────────────────────

  _getVariants() {
    if (this._variants) return this._variants;
    const script = this._form.querySelector('script[type="application/json"][data-variant-data]');
    if (!script) {
      this._variants = [];
      return this._variants;
    }
    try {
      this._variants = JSON.parse(script.textContent);
    } catch (e) {
      log('[Error] parsing variant data JSON:', e);
      this._variants = [];
    }
    return this._variants;
  }

  _getFormOptions() {
    const options = { option1: null, option2: null, option3: null };
    const rows = this._form.querySelectorAll(PRODUCT_OPTION_ATTR);
    rows.forEach((row, i) => {
      const select = row.querySelector('select');
      let value;
      if (select) {
        value = select.value;
      } else {
        const radio = row.querySelector('input[type="radio"]:checked');
        value = radio ? radio.value : null;
      }
      options['option' + (i + 1)] = value;
    });
    return options;
  }

  _computeActiveVariant() {
    const variants = this._getVariants();
    if (!variants.length) return null;
    if (variants.length === 1) return variants[0];
    const opts = this._getFormOptions();
    return (
      variants.find(
        (v) =>
          v.option1 === opts.option1 && v.option2 === opts.option2 && v.option3 === opts.option3
      ) || null
    );
  }

  // ── Change handling ───────────────────────────────────────────────────

  _onChange = (event) => {
    if (this._form.classList.contains(FORM_LOADING_CLASS)) return;

    const target = event.target;

    // Internal callers (e.g. hemming.openHemPanel after mounting the
    // panel) dispatch `change` on the form itself to request a
    // full state refresh. Treat that as the generic refresh signal.
    if (target === this._form) {
      this._refreshFormState();
      return;
    }

    if (
      target.matches(PRODUCT_OPTION_ATTR + ' select') ||
      target.matches(PRODUCT_OPTION_ATTR + ' input[type="radio"]') ||
      target.classList.contains('js-hemming-wrap') ||
      target.closest('.js-hemming-wrap')
    ) {
      this._refreshFormState();
      return;
    }

    if (target.matches(QUANTITY_SPINNER_SELECTOR + ' input[name="quantity"]')) {
      this._onQuantityChanged(target);
    }
  };

  _refreshFormState() {
    const variant = this._computeActiveVariant();
    const form = this._form;
    log('[Form] refresh', { variant: variant && variant.id });
    const variantInput = form.querySelector('input[name="id"]');
    const quantityInput = form.querySelector('input[name="quantity"]');
    const canHem = form.classList.contains('js-product-can-hem');
    const canChangeState = form.classList.contains('can-change-state');
    const hasVariant = variant != null;
    const variantQty = hasVariant ? variant.inventory_quantity : null;
    const canAddToBag = (() => {
      if (!hasVariant) return false;
      if (variant.inventory_policy === 'deny' && variant.inventory_management !== null) {
        return variantQty > 0;
      }
      return true;
    })();

    renderFormError(form, false);

    if (canHem) {
      hemmingHandleSizeChange(form, variant);
    }

    if (hasVariant) {
      form.classList.add(HAS_VARIANT_CLASS);
      // Load-bearing state first (variant id, URL), display niceties
      // last (price). If a later step ever throws, the form is still
      // submittable — fd.get('id') has the variant.
      if (variantInput) variantInput.value = variant.id;
      if (canChangeState && isAppHydrated()) {
        // Read from window.location.href, NOT appGet('url.currentObj.href').
        // The latter can be stale on initial product-page render after a
        // PJAX navigation from a collection page where a variant was
        // selected via quick-add — app.properties.url isn't always updated
        // before connectedCallback fires _refreshFormState, so we'd
        // replaceState the new product URL with the old collection URL.
        const currentUrl = new URL(window.location.href);
        if (currentUrl.searchParams.get('variant') !== String(variant.id)) {
          currentUrl.searchParams.set('variant', variant.id);
          window.app.replaceState('', '', currentUrl);
        }
      }
      // Intentionally no rewrite of .js-product-price on variant change:
      // the server-rendered price comes from the Markets-aware money
      // filter, while data-variant-data is shop-currency cents — a JS
      // rewrite would corrupt EUR/GBP storefronts. Trade-off: no instant
      // price update on variant change. Follow-up if needed: Section
      // Rendering API fetch of formatted-product-price.
      if (canAddToBag && variantQty > 0 && quantityInput) {
        const current = parseInt(quantityInput.value, 10) || 1;
        quantityInput.setAttribute('max', String(variantQty));
        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        if (current > variantQty) {
          quantityInput.value = String(variantQty);
          quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      toggleFormEnabled(form, canAddToBag, variant);
    } else {
      toggleFormEnabled(form, 'awaiting');
      form.classList.remove(HAS_VARIANT_CLASS);
      if (canChangeState && isAppHydrated()) {
        const url = new URL(window.location.href);
        url.searchParams.delete('variant');
        window.app.replaceState('', '', url.toString());
      }
    }

    this.dispatchEvent(
      new CustomEvent('variant:change', {
        bubbles: true,
        detail: { variant, form },
      })
    );

    this._maybeFastQuickAdd(form);
  }

  _maybeFastQuickAdd(form) {
    if (!appGet('flags.flags__ep_quick_add_enable_on_click')) return;
    // The faster-quick-add class is on the .js-quick-add-to-cart wrapper
    // (per quick-add-to-cart.liquid), not on the form itself.
    const quickAdd = form.closest(QUICK_ADD_SELECTOR);
    if (!quickAdd || !quickAdd.classList.contains('faster-quick-add')) return;
    if (!form.classList.contains(HAS_VARIANT_CLASS)) return;
    if (form.classList.contains('variant-is-unavailable')) return;
    if (form.classList.contains(FORM_LOADING_CLASS)) return;
    log('[Add to Cart] fast-quick-add: firing submit()');
    this.submit();
  }

  // ── Click handling: radio re-click deselect, sticky ATC, waitlist,
  //    quantity stepper, ATC button (delegated to native submit).
  // ──────────────────────────────────────────────────────────────────────

  _onClick = (event) => {
    const target = event.target;

    const radio = target.closest('input[type="radio"]');
    if (radio) {
      this._maybeRadioDeselect(radio);
      return;
    }

    if (target.closest(WAITLIST_BUTTON_SELECTOR)) {
      event.preventDefault();
      this._handleWaitlistClick();
      return;
    }

    const stepperBtn = target.closest(QUANTITY_SPINNER_SELECTOR + ' button');
    if (stepperBtn) {
      event.preventDefault();
      this._onQuantitySpinnerClicked(stepperBtn);
      return;
    }
  };

  _maybeRadioDeselect(radio) {
    if (!this._form.classList.contains(HAS_VARIANT_CLASS)) return;
    if (!radio.checked) return;
    setTimeout(() => {
      radio.checked = false;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }, 0);
  }

  // ── Quantity stepper ──────────────────────────────────────────────────

  _onQuantitySpinnerClicked(button) {
    const spinner = button.closest(QUANTITY_SPINNER_SELECTOR);
    if (!spinner) return;
    const input = this._form.querySelector('input[name="quantity"]');
    if (!input) return;
    const current = parseInt(input.value, 10) || 1;
    const max = parseInt(input.getAttribute('max'), 10) || Infinity;
    if (button.classList.contains('js-quantity-plus') && current + 1 <= max) {
      input.value = String(current + 1);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    } else if (button.classList.contains('js-quantity-minus') && current > 1) {
      input.value = String(current - 1);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  _onQuantityChanged(input) {
    const spinner = input.closest(QUANTITY_SPINNER_SELECTOR);
    if (!spinner) return;
    const quantity = parseInt(input.value, 10) || 0;
    const max = parseInt(input.getAttribute('max'), 10);
    const count = spinner.querySelector('.js-quantity-count');
    const plus = spinner.querySelector('.js-quantity-plus');
    const minus = spinner.querySelector('.js-quantity-minus');

    if (count) count.textContent = String(quantity);
    if (minus) minus.disabled = quantity <= 1;
    if (plus) {
      if (!Number.isFinite(max)) plus.disabled = false;
      else plus.disabled = quantity >= max;
    }
  }

  // ── Submit + ATC ──────────────────────────────────────────────────────

  _onSubmit = (event) => {
    event.preventDefault();
    const form = this._form;
    log('[Form] submit fired', { quickAdd: this._isQuickAdd() });

    if (form.classList.contains(FORM_LOADING_CLASS)) {
      log('[Form] already submitting, ignoring duplicate submission');
      return;
    }

    const fd = new FormData(form);
    const id = fd.get('id') || '';
    const hasHem = (fd.get('properties[Hem]') || '').toString().length > 0;
    const hasAcceptedHemmingTerms = fd.get('accept_hemming_terms') === '1';

    if (!id) {
      log('[Form] no variant id — surfacing size error');
      this._scrollToFirstEmptyOption();
      renderFormError(form, appGet('i18n.product.error.size'));
      return;
    }

    if (hasHem && !hasAcceptedHemmingTerms) {
      log('[Hemming] terms required — opening fork');
      // openHemTermsFork is async (Section Rendering API fetch on first
      // open). Lock the form so a re-click during the fetch can't fire a
      // second open or submit. The success path unlocks inside
      // openHemTermsFork once the modal is visible; the failure path
      // unlocks here via .catch.
      toggleFormEnabled(form, 'loading');
      openHemTermsFork().catch((err) => {
        log('[Form] hemming terms fork failed to open', err);
        toggleFormEnabled(form, true);
      });
      return;
    }
    const data = new URLSearchParams(fd).toString();
    if (salePolicyShouldShowModal(form, data)) {
      log('[Form] sale policy modal required');
      // openSalePolicyModal is async (Section Rendering API fetch on first
      // open). Lock the form NOW so a re-click during the fetch hits
      // FORM_LOADING_CLASS at the guard above instead of firing a second
      // open. Also pins the variant: the customer can't switch variants
      // mid-fetch and end up with disclaimer-vs-line-item-property mismatch.
      // The success path unlocks inside openSalePolicyModal once the modal
      // is visible; the failure path unlocks here via .catch.
      toggleFormEnabled(form, 'loading');
      openSalePolicyModal(form).catch((err) => {
        log('[Form] sale policy modal failed to open', err);
        toggleFormEnabled(form, true);
      });
      return;
    }

    toggleFormEnabled(form, 'loading');
    const quickAdd = this._isQuickAdd();
    log('[Add to Cart] ' + (quickAdd ? 'quick add via product card' : 'add via product page'), {
      title: this.getAttribute('data-product-title') || form.getAttribute('data-product-title'),
      variant: id,
    });

    addToCart(fd)
      .then((item) => {
        log('[Add to Cart] cart/add success', { variant: item.variant_id, qty: item.quantity });
        this._onAdded(item, fd, hasHem);
      })
      .catch((err) => {
        log('[Error] cart/add failed', err);
        this._onAddError(err);
      });
  };

  _scrollToFirstEmptyOption() {
    const form = this._form;
    const opts = this._getFormOptions();
    const emptyKey = ['option1', 'option2', 'option3'].find((k) => opts[k] == null);
    if (!emptyKey) return;
    const num = emptyKey.replace('option', '');
    const row = form.querySelector(`${PRODUCT_OPTION_ATTR}[data-option-num="${num}"]`);
    if (!row) return;
    const target = row.querySelector('select, input[type="radio"]');
    if (!target) return;
    if (typeof target.scrollIntoView === 'function') {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    if (typeof target.focus === 'function') target.focus();
  }

  _onAdded(item, fd, hasHem) {
    const form = this._form;
    const productName = (item.title || '').split(' ~ ')[0];
    const formatted = item.quantity > 1 ? `${item.quantity}x ${productName}` : productName;
    const addQuantity = parseInt(fd.get('quantity'), 10) || 1;

    const onCartChanged = () => {
      const cartDrawer = document.querySelector('cart-drawer');
      // Guarded read (appGet), not app.flags(): app.flags() → app.get() throws
      // pre-hydration. Safe here today (onCartChanged only fires post-add, after
      // hydration), but kept uniform so the pattern can't be copied into an
      // early path or reintroduce the throw.
      if (appGet('flags.flags__ep_cart_open_on_add_enable') === true) {
        if (cartDrawer && cartDrawer.isOpen !== true && cartDrawer.open) {
          window.app.ga('send', 'event', {
            eventCategory: 'MiniCart',
            eventAction: 'Opened - Automatically',
            eventLabel: 'Automatically upon add-to-cart',
          });
          cartDrawer.open();
        }
      } else {
        notifierPush({
          type: 'flash',
          content: (appGet('i18n.notifications.cart.added') || '').replace('{productName}', formatted),
          force: true,
          onClick: (event) => {
            event.preventDefault();
            if (cartDrawer && cartDrawer.open) cartDrawer.open();
            window.app?.ga('send', 'event', {
              eventCategory: 'MiniCart',
              eventAction: 'Opened - Notification Clicked',
            });
          },
        });
        window.app?.ga('send', 'event', {
          eventCategory: 'FlashMessage',
          eventAction: 'Shown',
          eventLabel: 'Item added',
        });
      }
    };

    log('[Add to Cart] complete', { title: productName, variant: item.variant_id });

    if (hasHem) {
      const hemVariant = parseInt(fd.get('hem_variant'), 10);
      // addToCart() already dispatched cart:update, so getCurrentCart()
      // returns the fresh cart. Synchronous read avoids a second round-trip.
      const cart = getCurrentCart();
      const hemInCart = cart && cart.items && cart.items.find((i) => i.variant_id === hemVariant);
      const quantity = hemInCart ? hemInCart.quantity + addQuantity : addQuantity;
      const updates = { [hemVariant]: quantity };
      log('[Add to Cart] (Hemming)', { variant: hemVariant, quantity });
      updateItems(updates).then(onCartChanged).catch((err) => {
        log('[Add to Cart] hemming update failed', err);
      });
    } else {
      // addToCart() already dispatched cart:update (sections included in
      // the POST), so cart state + badge are current. Call onCartChanged
      // directly — no second round-trip needed.
      onCartChanged();
    }

    this._resetAfterAdd(fd, hasHem);
    toggleFormEnabled(form, 'reset');

    // _resetAfterAdd dispatches `change` events to drive the usual
    // variant/URL reconciliation, but those are swallowed by _onChange's
    // form-loading guard — the loading class is only cleared by the 'reset'
    // call above, after the events have already fired. Without reconciliation
    // the URL keeps a stale ?variant= after the form is cleared. Reconcile
    // once against the final reset state: a multi-variant form that cleared
    // its selection resolves to no variant and drops ?variant=, while a
    // single-variant or hemmed add (where the selection is intentionally kept)
    // re-resolves its variant and correctly retains it. Quick-add forms keep
    // their selection and never touch the URL (can-change-state is false), so
    // skip them to avoid re-entering their post-add flow.
    if (!this._isQuickAdd()) {
      this._refreshFormState();
    }

    this.dispatchEvent(
      new CustomEvent('cart:added', {
        bubbles: true,
        detail: { item, form, hasHem },
      })
    );
  }

  _resetAfterAdd(fd, hasHem) {
    const form = this._form;
    if (this._isQuickAdd()) {
      // Don't reset the quick-add form fully — would trigger an infinite
      // loop with the radio-deselect / change cycle.
      form
        .querySelectorAll(
          'input:not([type="number"]):not([type="checkbox"]):not([type="radio"]):not([name="id"])'
        )
        .forEach((input) => {
          input.value = '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      return;
    }

    if (hasHem) {
      const hemProp = form.querySelector('input[name="properties[Hem]"]');
      if (hemProp) hemProp.value = '';
      const acceptHem = form.querySelector('input[name="accept_hemming_terms"]');
      if (acceptHem) acceptHem.checked = false;
      const hemVariantSel = form.querySelector('select[name="hem_variant"]');
      if (hemVariantSel) {
        hemVariantSel.value = '';
        hemVariantSel.dispatchEvent(new Event('change', { bubbles: true }));
      }
      closeHemPanel();
      return;
    }

    salePolicyResetForm(form);
    form.reset();
    form.querySelectorAll('select').forEach((select) => {
      const first = select.querySelector('option');
      if (first) select.value = first.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    });
    form.querySelectorAll('input[type="radio"]').forEach((radio) => {
      radio.checked = false;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    });
    form
      .querySelectorAll('input:not([type="number"]):not([type="checkbox"]):not([type="radio"])')
      .forEach((input) => {
        input.value = '';
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    closeHemPanel();
  }

  _onAddError(err) {
    const form = this._form;
    // `addToCart()` rejects with an Error whose `.response` is the
    // already-parsed JSON body and `.status` is the HTTP status (set in
    // cart-fetch's `post()` helper). No `responseText` parse here.
    const data = err && err.response ? err.response : null;
    const status = err && err.status;

    let message;
    if (data && data.message) {
      if (status === 422) {
        message = data.description;
        renderFormError(form, message);
        if (this._isQuickAdd()) {
          toggleFormEnabled(form, 'reset');
        } else {
          const m = (message || '').toLowerCase();
          if (m.includes('sold out') || m.includes('unavailable')) {
            toggleFormEnabled(form, false);
          } else {
            toggleFormEnabled(form, 'reset');
          }
        }
      }
    } else {
      message = (err && err.message) || 'An error occurred while adding the item to cart.';
      renderFormError(form, message);
      toggleFormEnabled(form, 'reset');
    }

    this.dispatchEvent(
      new CustomEvent('cart:error', {
        bubbles: true,
        detail: { message, response: data, form },
      })
    );
  }

  // ── Sticky ATC click forwarder ────────────────────────────────────────

  _bindStickyAtc() {
    if (this._isQuickAdd()) return;
    const product = this._form.closest(PRODUCT_SELECTOR);
    if (!product) return;
    const stickyButton = product.querySelector(STICKY_ATC_BUTTON_SELECTOR);
    if (!stickyButton) return;
    this._stickyAtcButton = stickyButton;
    this._stickyHandler = (event) => {
      const btn = event.target.closest('button');
      if (!btn) return;
      if (btn.matches(ADD_BUTTON_SELECTOR)) {
        log('[Add to Cart] sticky ATC button click');
        this._form.querySelector(ADD_BUTTON_SELECTOR)?.click();
      } else if (btn.matches(WAITLIST_BUTTON_SELECTOR)) {
        log('[Waitlist] sticky waitlist button click');
        this._form.querySelector(WAITLIST_BUTTON_SELECTOR)?.click();
      }
    };
    stickyButton.addEventListener('click', this._stickyHandler);
  }

  // ── Waitlist ──────────────────────────────────────────────────────────

  _handleWaitlistClick() {
    const form = this._form;
    if (form.classList.contains(FORM_LOADING_CLASS)) {
      log('[Waitlist] already in progress');
      return;
    }
    const isLoggedIn = isSignedIn();
    const productId = parseInt(this.getAttribute('data-product-id') || form.getAttribute('data-product-id'), 10);
    const productName = this.getAttribute('data-product-title') || form.getAttribute('data-product-title');
    const variant = this._computeActiveVariant();
    if (!variant) return;

    log('[Waitlist] click', { product: productId, variant: variant.id, loggedIn: isLoggedIn });

    if (isLoggedIn) {
      const email = appGet('customer.email');
      window.app?.ga('send', 'event', { eventCategory: 'Waitlist', eventAction: 'Clicked' });
      this._registerWaitlist(email, productName, productId, variant.id);
    } else {
      const sessionData = {
        redirect: appGet('url.current'),
        callback: { args: [productName, productId, variant.id] },
      };
      notifierPush({
        type: 'flash',
        content: appGet('i18n.notifications.account.force_login'),
        force: true,
      });
      window.app?.ga('send', 'event', {
        eventCategory: 'Waitlist',
        eventAction: 'Clicked - Redirect to Login',
      });
      try {
        sessionStorage.setItem(WAITLIST_STORAGE_KEY, JSON.stringify(sessionData));
      } catch (err) {
        log('[Waitlist] sessionStorage unavailable', err);
      }
      // Back to this page after login, where _postLoginWaitlistRecovery()
      // finds the stashed signup and registers it.
      window.location.assign(
        loginUrlReturningTo(window.location.pathname + window.location.search)
      );
    }
  }

  _registerWaitlist(email, productName, productId, variantId) {
    const form = this._form;
    toggleFormEnabled(form, 'loading');

    const done = (onWaitlist) => {
      toggleFormEnabled(form, false);
      if (onWaitlist) {
        const btn = form.querySelector(WAITLIST_BUTTON_SELECTOR);
        if (btn) markWaitlistButton(btn);
      }
    };

    // Every exit from here has to clear `form-loading` and say something.
    // The button advertises that it captured a signup, so a silent no-op
    // reads as success to the shopper and records no demand signal for us.
    const showError = (msg) => {
      done(false);
      notifierPush({ type: 'flash', content: msg, force: true });
    };

    if (!productId || !variantId) {
      log('[Waitlist] missing product/variant', { productId, variantId });
      showError(waitlistErrorMessage(null));
      return;
    }

    bisAddToWaitlist({
      product_id: productId,
      variant_id: variantId,
      // A blank email is rejected by the API wrapper as `no_email` rather
      // than letting BIS ship a signup with neither email nor channel.
      customer_email: email,
      success: () => {
        log('[Waitlist] success', { variant: variantId });
        done(true);
        addToWaitlistCache(variantId);
        notifierPush({
          type: 'flash',
          content: (appGet('i18n.notifications.waitlist.added') || '').replace(
            '{productName}',
            productName
          ),
          force: true,
        });
        window.app?.ga('send', 'event', { eventCategory: 'Waitlist', eventAction: 'Success' });
      },
      error: (response) => {
        const isDuplicate = !!(response && response.errors && response.errors.email);
        const msg = waitlistErrorMessage(response);
        if (isDuplicate) {
          log('[Waitlist] already registered', { variant: variantId });
          done(true);
          addToWaitlistCache(variantId);
          notifierPush({
            type: 'flash',
            content: (appGet('i18n.notifications.waitlist.already_registered') || '').replace(
              '{productName}',
              productName
            ),
            force: true,
          });
          return;
        }
        log('[Waitlist] failed', msg);
        showError(msg);
      },
    });
  }

  _postLoginWaitlistRecovery() {
    const stored = sessionStorage.getItem(WAITLIST_STORAGE_KEY);
    if (!stored) return;
    // Pre-hydration the customer id is unknowable; retry once on ep:ready
    // instead of reading "not signed in" and dropping the pending signup.
    if (!isAppHydrated()) {
      document.addEventListener('ep:ready', () => this._postLoginWaitlistRecovery(), {
        once: true,
      });
      return;
    }
    if (!isSignedIn()) return;

    sessionStorage.removeItem(WAITLIST_STORAGE_KEY);
    let waitlistData;
    try {
      waitlistData = JSON.parse(stored);
    } catch (e) {
      return;
    }
    const args = waitlistData.callback?.args || [];
    const productId = parseInt(args[1], 10);
    if (!productId) return;

    // Only the form whose product matches is the right one to register.
    const myProductId = parseInt(this.getAttribute('data-product-id') || this._form.getAttribute('data-product-id'), 10);
    if (myProductId !== productId) {
      // This isn't our form — re-stash so the right form picks it up.
      try {
        sessionStorage.setItem(WAITLIST_STORAGE_KEY, stored);
      } catch (e) {
        /* noop */
      }
      // Navigate back to the redirect URL on a one-shot turbo:load.
      const redirect = waitlistData.redirect;
      if (redirect && redirect !== window.location.href) {
        pjaxUrl(redirect);
      }
      return;
    }

    log('[Waitlist] post-login recovery', { product: productId, variant: args[2] });
    window.app?.ga('send', 'event', { eventCategory: 'Waitlist', eventAction: 'Successful Login' });
    const email = appGet('customer.email');
    this._registerWaitlist(email, args[0], productId, parseInt(args[2], 10));
  }

  // ── Coming-soon: disable inputs ───────────────────────────────────────

  _handleComingSoon() {
    const product = this._form.closest(PRODUCT_SELECTOR);
    if (!product || !product.classList.contains(COMING_SOON_CLASS)) return;
    this._form
      .querySelectorAll('input[type="radio"], select, .js-add-hemming')
      .forEach((el) => {
        el.disabled = true;
      });
    this._form.querySelectorAll('input[type="radio"]').forEach((r) => (r.checked = false));
  }

  // ── Mark unavailable radios + select options ──────────────────────────

  _loadUnavailableRadios() {
    const variants = this._getVariants();
    if (!variants.length) return;
    const rows = this._form.querySelectorAll('.js-product-option-row');
    if (rows.length !== 1) return;
    rows.forEach((row) => {
      const optionNum = 'option' + row.getAttribute('data-option-num');
      row.querySelectorAll('input[type="radio"]').forEach((radio) => {
        const val = radio.value;
        const variant = variants.find((v) => v[optionNum] === val);
        if (!variant) return;
        const shouldDisable =
          variant.inventory_quantity <= 0 &&
          variant.inventory_management !== null &&
          variant.inventory_policy === 'deny';
        const parent = radio.parentElement;
        if (!parent) return;
        parent.classList.toggle(OPTION_UNAVAILABLE_CLASS, shouldDisable);
      });
    });
  }

  _loadUnavailableSelectOptions() {
    // Runs from connectedCallback, which on a cold page load upgrades during
    // HTML parsing — before DOMContentLoaded, i.e. before app.init() hydrates
    // app.properties and dispatches ep:ready. The waitlist flag and i18n
    // disclaimer strings read below only resolve post-hydration, so when called
    // pre-hydration, defer a single re-run to ep:ready rather than mark options
    // with an empty disclaimer (this also sidesteps the app.get() pre-init
    // throw entirely). On Turbo navigations the element reconnects
    // post-hydration, so isAppHydrated() is true and it runs immediately.
    if (!isAppHydrated()) {
      document.addEventListener('ep:ready', () => this._loadUnavailableSelectOptions(), {
        once: true,
      });
      return;
    }
    // Source variants from the in-form <script data-variant-data> element via
    // _getVariants() — the SAME source the radio path (_loadUnavailableRadios)
    // uses (the form snippet no longer renders a `data-variants` attribute).
    const variants = this._getVariants();
    if (!variants.length) return;
    const i18nWaitlist = appGet('i18n.product.option.waitlist');
    const i18nOOS = appGet('i18n.product.option.out_of_stock');
    // Read the flag through the guarded appGet reader rather than the legacy
    // app.flags helper: even past the hydration guard above, appGet keeps this
    // consistent with the i18n reads and resilient if the call site ever moves.
    const isWaitlistEnabled = appGet('flags.flags__backinstock_enable') === true;
    const suffix = isWaitlistEnabled && i18nWaitlist ? i18nWaitlist : i18nOOS;

    this._form.querySelectorAll('.js-product-option-row').forEach((row) => {
      const optionNum = 'option' + row.getAttribute('data-option-num');
      const select = row.querySelector('select');
      if (!select) return;
      const byValue = {};
      variants.forEach((v) => {
        byValue[v[optionNum]] = v;
      });
      select.querySelectorAll('option[value]').forEach((option) => {
        const val = option.getAttribute('value');
        const variant = byValue[val];
        if (!variant) return;
        const isUnavailable =
          variant.inventory_quantity <= 0 &&
          variant.inventory_management !== null &&
          variant.inventory_policy === 'deny';
        if (option.classList.contains(OPTION_UNAVAILABLE_CLASS) === isUnavailable) return;
        // Preserve the server's pretty-formatting (".5" → "½") from
        // get-product-select-options.liquid when we rewrite the option text.
        const label = val.replace(/\.5/g, '½');
        // Do NOT set option.disabled — sold-out options must remain
        // selectable so the waitlist flow can engage. See the IIFE
        // commit history for the full rationale.
        if (isUnavailable) {
          option.classList.add(OPTION_UNAVAILABLE_CLASS);
          // suffix carries a visually-hidden .option--disclaimer span (the
          // i18n {{optionName}} placeholder hydrates to empty), so the option
          // shows just the label with the CSS strikethrough; OS-native pickers
          // that ignore the span styling still surface the disclaimer text.
          option.innerHTML = label + (suffix || '');
        } else {
          option.classList.remove(OPTION_UNAVAILABLE_CLASS);
          option.innerHTML = label;
        }
      });
    });
  }

  // ── Helpers ───────────────────────────────────────────────────────────

  _isQuickAdd() {
    return appGet('page_type') !== 'product' && !!this.closest('.js-product-card');
  }
}

customElements.define('add-item-form', AddItemForm);
log('[Form] <add-item-form> registered');

export { AddItemForm };
